import assign from 'object-assign';
import {COOKIES} from '@util/StorageUtils';
import Cookie from 'js-cookie';
import namespace from '@root/src/scripts/upi/namespace';

const UNKNOWN = 'unspecified';

/**
 * activate
 * Set a cookie that can then be used as a feature check within modules
 * to determine an implementation to render for ABTests ran from AB testing tools
 * @param data = {key:String, name:String, description:String, implementation:String, page:String, dateRange:String}
 * example usage
 * activate( {
        key: 'SyncedCartButton',
        name: 'Reorder cart button',
        page: 'site wide',
        description: 'Implements an add to cart button that is synced with current cart on reorder page',
        implementation: 'deploys upi-frontend@1.6.3 with implementation under "upiABTest.SyncedAddButton" feature flag',
        dateRange: '10/20/2020 - 11/20/2020'
    } );
 */
function activate( data ) {
    const tests = getActiveTests() || [];

    if ( !data.key ) {
        throw Error( 'ABTest.activate requires a "key" parameter to be specified' );
    }

    if ( !isActive( data.key ) ) {
        tests.push( data );
        setActiveTests( tests );
    }

    logTestDetail( data );
    return true;
}

function deactivate( key ) {
    const tests = getActiveTests() || [];

    if ( !key ) {
        throw Error( 'ABTest.function deactivate( data ) {\n requires a "key" parameter to be specified' );
    }

    const activeTests = tests.filter( t => t.key !== key );

    // if change detected
    if ( tests.length !== activeTests.length ) {
        setActiveTests( activeTests );
        console.log( `Deactivated test with key "${key}"` );
        return true;
    }

    console.log( 'There were no active tests with this key...' );
    return null;
}

function clearAll() {
    const runningTests = getActiveTests();

    if ( runningTests.length > 0 ) {
        Cookie.remove( COOKIES.AB_TESTS.name );
        console.group( 'Deactivated the following test keys:' );
        runningTests.forEach( t => console.log( t.key ) );
        console.groupEnd();
        return true;
    }

    console.log( 'There were no active test keys...' );
    return null;
}

/**
 * Return an array of active tests, Array.empty if none
 * @returns {*|*[]}
 */
function getActiveTests() {
    const activeTests = Cookie.get( COOKIES.AB_TESTS.name );

    if ( !activeTests ) {
        return [];
    }

    try {
        return JSON.parse( activeTests );
    }
    catch ( error ) {
        return [];
    }
}

function setActiveTests( tests ) {
    Cookie.set( COOKIES.AB_TESTS.name, JSON.stringify( tests ), {expires: 1} );
}

/**
 * Checks if a test key is stored for current sessions to be used as feature check from components
 * @param key
 * @returns {boolean}
 */
function isActive( key ) {
    const tests = getActiveTests() || [];
    const foundTest = tests.find( t => t.key === key );
    return Boolean( foundTest );
}

function logTestDetail( {key, name, description, implementation, page, dateRange} ) {
    console.groupCollapsed( `A/B test running: ${name || key}` );
    console.table( {
        'TestKey (cookie)': key,
        'Description': description || UNKNOWN,
        'Affected page': page || UNKNOWN,
        'Implementation': implementation || UNKNOWN,
        'Expected date range': dateRange || UNKNOWN
    } );
    console.groupEnd();
}

function logActiveTests() {
    getActiveTests().forEach( logTestDetail );
}

const publicApi = {activate, deactivate, isActive, logActiveTests, getActiveTests, clearAll};
assign( namespace( 'ABTests' ), publicApi );

export default publicApi;




